/**
 * Carbon Preset — High-quality woven carbon fibre material.
 *
 * Uses a procedural CarbonTexture as the albedo map so no static texture
 * file is required. Two variants:
 *
 *  glossy — high clearcoat resin finish (high-end visible-carbon panels)
 *  matte  — no clearcoat, higher roughness (structural carbon, interior trim)
 *
 * Carbon fibre is a dielectric (metalness = 0). All visual "specular" shine
 * comes from the clearcoat layer that simulates the resin coating.
 *
 * The `colorCode` parameter is intentionally ignored — carbon fibre colour
 * is entirely determined by the texture and material properties.
 *
 * @see docs/ims-engine-api/06-feature-color-system.md
 */

import * as THREE from 'three';
import type { CarbonType } from '../../api/types';
import { CarbonTexture } from '../textures/CarbonTexture';
import { devWarn } from '../../utils/devLog';

export type { CarbonType };

// ---------------------------------------------------------------------------
// PBR value definitions
// ---------------------------------------------------------------------------

export interface CarbonValues {
  /** Carbon fibre is always non-metallic — metalness = 0. */
  metalness: number;
  roughness: number;
  clearcoat: number;
  clearcoatRoughness: number;
}

/**
 * Calibrated PBR values per carbon finish type.
 *
 * glossy: represents the shiny resin-saturated look of exposed carbon panels.
 *         High clearcoat simulates the transparent lacquer/epoxy layer.
 * matte:  represents dry or low-gloss carbon, e.g. interior trim or structural
 *         parts. No clearcoat — surface properties dominate.
 */
export const CARBON_PRESETS: Record<CarbonType, CarbonValues> = {
  glossy: {
    metalness:          0.15,
    roughness:          0.4,
    clearcoat:          1.0,
    clearcoatRoughness: 0.02,
  },
  matte: {
    metalness:          0.15,
    roughness:          0.4,
    clearcoat:          1.0,
    clearcoatRoughness: 0.5,
  },
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create a new Carbon MeshPhysicalMaterial.
 *
 * @param _colorCode - Ignored for carbon (texture determines all colour)
 * @param type       - Finish variant: 'glossy' | 'matte'. Default: 'glossy'
 */
export function createCarbonMaterial(
  _colorCode: string,
  type: CarbonType | string = 'glossy',
): THREE.MeshPhysicalMaterial {
  const preset = CARBON_PRESETS[type as CarbonType] ?? (() => {
    devWarn('createCarbonMaterial()', `unknown carbon type "${type}" — falling back to "glossy".`, {
      'Received type': type,
      'Valid types':   Object.keys(CARBON_PRESETS),
    });
    return CARBON_PRESETS.glossy;
  })();

  // Procedural albedo texture — tileable diagonal 2×2 twill weave
  const carbonCanvas = new CarbonTexture(256) as unknown as HTMLCanvasElement;
  const map = new THREE.CanvasTexture(carbonCanvas);
  map.wrapS     = THREE.RepeatWrapping;
  map.wrapT     = THREE.RepeatWrapping;
  map.repeat.set(0.5, 0.5);  // 6×6 tiling looks natural for body-panel scale
  map.anisotropy = 16;

  return new THREE.MeshPhysicalMaterial({
    // White base colour so the dark texture is not further tinted
    color: new THREE.Color(0x6B6B6B),
    map,
    metalness:          preset.metalness,
    roughness:          preset.roughness,
    clearcoat:          preset.clearcoat,
    clearcoatRoughness: preset.clearcoatRoughness,
  });
}

/**
 * Return the PBR values for a given carbon finish type.
 * Falls back to 'glossy' for unknown types.
 */
export function getCarbonPreset(type: CarbonType | string): CarbonValues {
  const preset = CARBON_PRESETS[type as CarbonType];
  if (!preset) {
    devWarn('getCarbonPreset()', `unknown carbon type "${type}" — falling back to "glossy".`, {
      'Received type': type,
      'Valid types':   Object.keys(CARBON_PRESETS),
    });
    return { ...CARBON_PRESETS.glossy };
  }
  return { ...preset };
}
